/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { setDiff } from '@ember/object/computed';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

export default class GroupsController extends Controller {
  // Services
  @service
  paperToaster;

  @service
  session;

  @service
  store;

  @tracked
  groupName = '';

  @action
  setGroupName(value) {
    this.groupName = value;
  }

  @tracked
  groupKey = '';

  @action
  setGroupKey(value) {
    this.groupKey = value;
  }

  @tracked
  groupDescription = '';

  @action
  setGroupDescription(value) {
    this.groupDescription = value;
  }

  @tracked
  groupId = '';

  @tracked
  groupDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  deleteGroupDialogOpen = false;

  @tracked
  deleteInProgress = false;

  @tracked
  errorMessage = '';

  @setDiff('model.allGroups', 'model.groups')
  availableGroups;

  @tracked
  selectedGroup = null;

  isSortable = false;

  @tracked
  groupSearchText;

  @tracked
  groupToDelete;

  // toggleDialog opens the group dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('groupDialogOpen');
    if (!this.groupDialogOpen) {
      this.resetForm();
    }
  }

  @action
  toggleGroupDialog() {
    this.toggleDialog();
  }

  @action
  addExistingGroup(group) {
    group
      .get('templateSetIds')
      .then((templateSetIds) => {
        templateSetIds.push(this.get('model.templateSet'));
        return group.save();
      })
      .then(() => {
        this.get('model.groups').update();
      });
    this.toggleDialog();
  }

  @action
  editGroup(id) {
    this.store.findRecord('people-schema/group', id).then((group) => {
      this.groupName = group.get('name');
      this.groupKey = group.get('key');
      this.groupDescription = group.get('description');
    });
    this.groupId = id;
    this.toggleDialog();
  }

  @action
  saveGroup() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';

    if (isEmpty(this.groupId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('people-schema/group', {
          name: this.groupName,
          key: this.groupKey,
          description: this.groupDescription,
          sequence: this.get('model.groups.length') + 1,
          personType: this.get('model.templateSet.personType'),
        });
        record.get('templateSetIds').then((templateSetIds) => {
          templateSetIds.push(this.get('model.templateSet'));
          resolve(record);
        });
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.groupId;
        this.store.findRecord('people-schema/group', id).then((group) => {
          group.set('name', this.groupName);
          group.set('key', this.groupKey);
          group.set('description', this.groupDescription);

          resolve(group);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          this.groupDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          record.rollbackAttributes();
        })
        .then(() => {
          this.saveInProgress = false;
          this.get('model.groups').update();
        });
    });
  }

  @action
  deleteGroup(id) {
    this.errorMessage = '';
    this.store.findRecord('people-schema/group', id).then((group) => {
      this.groupToDelete = group;
    });
    this.deleteGroupDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        let group = this.groupToDelete;

        // Deletion confirmed; actually delete (or disconnect) the record.
        this.deleteInProgress = true;

        if (group.get('templateSetIds.length') === 1) {
          // Delete record (locally + pushed to server).
          return group
            .destroyRecord()
            .then(resolve)
            .catch((err) => {
              // Remove 'deleted' flag so that entity can be interacted with again.
              group.rollbackAttributes();
              reject(err);
            });
        } else {
          let groupFields = this.get('model.allFields').filter(
            (f) => f.templateGroupId?.id === group.id,
          );
          if (groupFields.length) {
            reject({
              errors: {
                msg: 'This group cannot be removed from this Template, as it contains fields.  Please delete all fields within this group, then try again.',
              },
            });
          } else {
            let templateSet = this.get('model.templateSet');
            group
              .get('templateSetIds')
              .then((templateSetIds) => {
                let templateSetIndex = templateSetIds.indexOf(templateSet);
                if (templateSetIndex !== -1) {
                  templateSetIds.splice(templateSetIndex, 1);
                }
                return group.save();
              })
              .then(() => {
                this.get('model.groups').update();
                resolve();
              })
              .catch((err) => {
                // Put the template set back, or a retry would see this as the
                // group's last template and delete a group others still use.
                rollbackRecord(group);
                reject(err);
              });
          }
        }
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteGroupDialogOpen = false;
        this.groupToDelete = null;
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else {
      this.errorMessage = err.message;
    }
  }

  setErrorMessage(code, action) {
    var message = 'An unknown error occurred.  Please try again.';
    const codes = {
      400: 'Sorry, your request was invalid.  Please check all details below, and try again.',
      403: {
        create:
          'Sorry, you are not authorised to create a new user.  Please check that you are logged in as the correct user, and try again.',
        update:
          'Sorry, you are not authorised to edit this user.  Please check that you are logged in as the correct user, and try again.',
        delete:
          'Sorry, you are not authorised to delete this user.  Please check that you are logged in as the correct user, and try again.',
      },
      409: 'A user already exists with that username.  Please double-check the details, or find and update the existing user.',
    };

    if (!isEmpty(codes[code][action])) {
      message = codes[code][action];
    } else if (!isEmpty(codes[code])) {
      message = codes[code];
    }

    this.errorMessage = message;
  }

  resetForm() {
    this.groupId = '';
    this.groupName = '';
    this.groupKey = '';
    this.groupDescription = '';
    this.selectedGroup = null;
    this.groupSearchText = null;
  }
}
